import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  TextField,
  Chip
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { BarChart } from '@mui/x-charts/BarChart';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { getAuthHeaders } from '../../../utils/authUtils';
import { createApiUrl } from '../../../utils/apiConfig';
import { useSnackbar } from '../../CentralSnackbar';
import { useFormatConfig, formatDateSync } from '../../../utils/dateTimeFormat';

const Addenda = forwardRef(({ currentUser }, ref) => {
  const { t } = useTranslation();
  const { showError } = useSnackbar();
  const { isLoaded: isFormatConfigLoaded } = useFormatConfig();
  const chartRef = useRef(null);
  
  // State
  const [addendaData, setAddendaData] = useState(null);
  const [addendaLoading, setAddendaLoading] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [timeFilter, setTimeFilter] = useState('month'); // Default: Monat
  const [startPeriod, setStartPeriod] = useState('');
  const [endPeriod, setEndPeriod] = useState('');
  
  // Helper: Get current period based on timeFilter
  const getCurrentPeriod = (filterType) => {
    const now = new Date();
    switch (filterType) {
      case 'date':
        return now.toISOString().split('T')[0]; // YYYY-MM-DD
      case 'week': {
        const year = now.getFullYear();
        const oneJan = new Date(year, 0, 1);
        const numberOfDays = Math.floor((now - oneJan) / (24 * 60 * 60 * 1000));
        const weekNumber = Math.ceil((numberOfDays + oneJan.getDay() + 1) / 7);
        return `${year}-W${String(weekNumber).padStart(2, '0')}`;
      }
      case 'month':
        return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
      case 'quarter': {
        const quarter = Math.ceil((now.getMonth() + 1) / 3);
        return `${now.getFullYear()}-Q${quarter}`;
      }
      case 'year':
        return `${now.getFullYear()}`;
      default:
        return '';
    }
  };

  // Helper: Get next/previous period
  const getAdjacentPeriod = (period, filterType, direction) => {
    if (!period) return period;
    
    switch (filterType) {
      case 'date': {
        const date = new Date(period);
        date.setDate(date.getDate() + direction);
        return date.toISOString().split('T')[0];
      }
      case 'week': {
        const [year, week] = period.split('-W').map(Number);
        let newWeek = week + direction;
        let newYear = year;
        
        if (newWeek < 1) {
          newYear--;
          newWeek = 52; // Approximation
        } else if (newWeek > 52) {
          newYear++;
          newWeek = 1;
        }
        return `${newYear}-W${String(newWeek).padStart(2, '0')}`;
      }
      case 'month': {
        const [year, month] = period.split('-').map(Number);
        let newMonth = month + direction;
        let newYear = year;
        
        if (newMonth < 1) {
          newYear--;
          newMonth = 12;
        } else if (newMonth > 12) {
          newYear++;
          newMonth = 1;
        }
        return `${newYear}-${String(newMonth).padStart(2, '0')}`;
      }
      case 'quarter': {
        const [year, quarter] = period.split('-Q').map(Number);
        let newQuarter = quarter + direction;
        let newYear = year;
        
        if (newQuarter < 1) {
          newYear--;
          newQuarter = 4;
        } else if (newQuarter > 4) {
          newYear++;
          newQuarter = 1;
        }
        return `${newYear}-Q${newQuarter}`;
      }
      case 'year': {
        const year = parseInt(period);
        return `${year + direction}`;
      }
      default:
        return period;
    }
  };

  // Expose download functions to parent component via ref
  useImperativeHandle(ref, () => ({
    handlePdfDownload,
    handleCsvDownload
  }));

  // Set default period on mount
  useEffect(() => {
    const currentPeriod = getCurrentPeriod(timeFilter);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  }, []);

  // Wheel event for period scrolling (Shift + Scroll)
  useEffect(() => {
    const handleWheel = (event) => {
      if (event.shiftKey && (startPeriod || endPeriod)) {
        event.preventDefault();
        
        const direction = event.deltaY > 0 ? -1 : 1; // Down = previous, Up = next
        
        setStartPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
        setEndPeriod(prev => getAdjacentPeriod(prev, timeFilter, direction));
      }
    };

    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [timeFilter, startPeriod, endPeriod]);

  // Load Users on mount
  useEffect(() => {
    loadUsers();
  }, []);

  // Load Addenda data when users are selected
  useEffect(() => {
    if (selectedUsers.length > 0) {
      loadAddendaData();
    }
  }, [selectedUsers]);

  const loadUsers = async () => {
    try {
      const response = await fetch(createApiUrl('users/'), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        // Filter nur AKTIVE User aus Gruppe 1-5
        const filteredUsers = data.filter(user => 
          user.group_level >= 1 && 
          user.group_level <= 5 && 
          user.is_active === true
        );
        setAllUsers(filteredUsers);
        // Wähle alle gefilterten User standardmäßig aus
        setSelectedUsers(filteredUsers);
        } else {
        showError(t('statistics.team.loadUsersError', 'Fehler beim Laden der Benutzer'), 'ERR-STATS-TEAM-ADD-USERS');
      }
    } catch (error) {
      console.error('Error loading users:', error);
      showError(t('statistics.team.loadUsersError', 'Fehler beim Laden der Benutzer'), 'ERR-STATS-TEAM-ADD-USERS-002');
    }
  };

  const loadAddendaData = async () => {
    if (selectedUsers.length === 0) return;
    
    setAddendaLoading(true);
    try {
      const userIds = selectedUsers.map(u => u.id).join(',');
      const response = await fetch(createApiUrl(`statistics/team/addenda?user_ids=${userIds}`), {
        headers: { ...getAuthHeaders() }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('=== TEAM ADDENDA: Received data:', data);
        console.log('=== TEAM ADDENDA: User data keys:', Object.keys(data.users || {}));
        setAddendaData(data.users);
      } else {
        showError(t('statistics.team.addenda.loadError', 'Fehler beim Laden der Addenda'), 'ERR-STATS-TEAM-ADD-001');
      }
    } catch (error) {
      console.error('Error loading Addenda data:', error);
      showError(t('statistics.team.addenda.loadError', 'Fehler beim Laden der Addenda'), 'ERR-STATS-TEAM-ADD-002');
    } finally {
      setAddendaLoading(false);
    }
  };

  const handleTimeFilterChange = (event) => {
    const newFilterType = event.target.value;
    setTimeFilter(newFilterType);
    // Setze die Perioden auf den aktuellen Wert für den neuen Filter
    const currentPeriod = getCurrentPeriod(newFilterType);
    setStartPeriod(currentPeriod);
    setEndPeriod(currentPeriod);
  };

  const handleStartPeriodChange = (event) => {
    setStartPeriod(event.target.value);
  };

  const handleEndPeriodChange = (event) => {
    setEndPeriod(event.target.value);
  };

  // Helper function to format period for display
    const formatPeriod = (period) => {
      if (!period) return period;
      try {
        switch (timeFilter) {
          case 'date':
            if (!isFormatConfigLoaded) return period;
            return formatDateSync(period);
          case 'week':
            const weekMatch = period.match(/(\d{4})-W(\d{2})/);
            if (weekMatch) return `KW${weekMatch[2]} - ${weekMatch[1]}`;
            return period;
          case 'month':
            const monthMatch = period.match(/(\d{4})-(\d{2})/);
            if (monthMatch) return `${monthMatch[2]}.${monthMatch[1]}`;
            return period;
          case 'quarter':
            const quarterMatch = period.match(/(\d{4})-Q(\d)/);
            if (quarterMatch) return `Q${quarterMatch[2]} - ${quarterMatch[1]}`;
            return period;
          case 'year':
            return period;
          default:
            return period;
        }
      } catch (error) {
        return period;
      }
    };

  // Calculate available periods based on addendaData
  const getAvailablePeriods = () => {
    const periods = new Set();
    
    if (!addendaData) return [];

    Object.values(addendaData).forEach(userData => {
      if (!userData || !userData.addenda) return;

      const addPeriods = (data) => {
        if (data) {
          data.forEach(d => periods.add(d.period));
        }
      };
      
    switch (timeFilter) {
        case 'date': addPeriods(userData.addenda.by_date); break;
        case 'week': addPeriods(userData.addenda.by_week); break;
        case 'month': addPeriods(userData.addenda.by_month); break;
        case 'quarter': addPeriods(userData.addenda.by_quarter); break;
        case 'year': addPeriods(userData.addenda.by_year); break;
        default: break;
      }
    });
    
    return Array.from(periods).sort();
  };
  
  const availablePeriods = getAvailablePeriods();

  // Prepare chart data - ein Datenpunkt pro User
  const getChartData = () => {
    if (!addendaData || Object.keys(addendaData).length === 0) return [];

    const chartData = [];

    selectedUsers.forEach(user => {
      const userData = addendaData[user.id];
      if (!userData) return;

      // Formatiere den Namen mehrzeilig: Titel in erster Zeile, Name in zweiter
      const title = user.academic_degree || '';
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const userName = title ? `${title}\n${fullName}` : fullName;

      // Berechne Total Count für den gewählten Zeitraum
      let data = [];
      switch (timeFilter) {
        case 'date': data = userData.addenda.by_date; break;
        case 'week': data = userData.addenda.by_week; break;
        case 'month': data = userData.addenda.by_month; break;
        case 'quarter': data = userData.addenda.by_quarter; break;
        case 'year': data = userData.addenda.by_year; break;
        default: break;
      }

      // Filter nach Zeitraum
      if (startPeriod || endPeriod) {
        data = data.filter(item => {
          if (startPeriod && endPeriod) {
            return item.period >= startPeriod && item.period <= endPeriod;
    } else if (startPeriod) {
            return item.period >= startPeriod;
          } else if (endPeriod) {
            return item.period <= endPeriod;
          }
          return true;
        });
      }

      const addendaCount = data.reduce((sum, item) => sum + item.count, 0);

      chartData.push({
        user: userName,
        userId: user.id,
        addendaCount: addendaCount
      });
    });

    return chartData;
  };

  const chartData = getChartData();

  // CSV Export Function
  const handleCsvDownload = () => {
    if (!addendaData || selectedUsers.length === 0) return;

    // Hole alle verfügbaren Perioden
    const allPeriods = availablePeriods.filter(period => {
      if (startPeriod && period < startPeriod) return false;
      if (endPeriod && period > endPeriod) return false;
      return true;
    });

    // Header-Zeile
    let csv = '\uFEFF'; // BOM for Excel UTF-8
    csv += '"' + t('statistics.team.fallzahlen.period', 'Zeitraum') + '";';
    
    // Für jeden User eine Spalte
    selectedUsers.forEach(user => {
      const userName = `${user.academic_degree || ''} ${user.first_name} ${user.last_name}`.trim();
      csv += '"' + userName + '";';
    });
    csv += '\n';

    // Datenzeilen
    allPeriods.forEach(period => {
      csv += '"' + formatPeriod(period) + '";';
      
      selectedUsers.forEach(user => {
        const userData = addendaData[user.id];
        if (!userData || !userData.addenda) {
          csv += '0;';
          return;
        }

        let dataArray = [];
        switch (timeFilter) {
          case 'date': dataArray = userData.addenda.by_date; break;
          case 'week': dataArray = userData.addenda.by_week; break;
          case 'month': dataArray = userData.addenda.by_month; break;
          case 'quarter': dataArray = userData.addenda.by_quarter; break;
          case 'year': dataArray = userData.addenda.by_year; break;
          default: break;
        }

        const periodData = dataArray.find(d => d.period === period);
        csv += (periodData ? periodData.count : 0) + ';';
      });
      
      csv += '\n';
    });

    // Download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'team_addenda_statistics.csv';
    link.click();
    document.body.removeChild(link);
  };

  // PDF Export Function
  const handlePdfDownload = async () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    
    // Hole Benutzerinformationen aus localStorage
    const userStr = localStorage.getItem('user');
    const currentUser = userStr ? JSON.parse(userStr) : null;
    const userName = currentUser 
      ? `${currentUser.academic_degree || ''} ${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim()
      : '';
    
    // Funktion für Fußzeile
    const addFooter = () => {
      const now = new Date();
      const dateStr = isFormatConfigLoaded ? formatDateSync(now.toISOString().split('T')[0]) : now.toLocaleDateString();
      const timeStr = now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
      const footerText = `${t('statistics.team.addenda.pdfTitle', 'Team Report - Addenda')}, ${t('statistics.personal.pdf.createdBy', 'erstellt von')} ${userName}, ${t('statistics.personal.pdf.on', 'am')} ${dateStr} ${t('statistics.personal.pdf.at', 'um')} ${timeStr}`;
      
      doc.setFontSize(8);
      doc.setTextColor(128, 128, 128);
      doc.text(footerText, 14, pageHeight - 10);
      doc.setTextColor(0, 0, 0);
    };
    
    // Titel (linkszentriert)
    doc.setFontSize(16);
    doc.text(t('statistics.team.addenda.pdfTitle', 'Team Report - Addenda'), 14, 15);
    
    // Zeitraum-Info
    let yPosition = 25;
    doc.setFontSize(10);
    const timeFilterLabels = {
      'date': t('statistics.team.fallzahlen.day', 'Tag'),
      'week': t('statistics.team.fallzahlen.week', 'Woche'),
      'month': t('statistics.team.fallzahlen.month', 'Monat'),
      'quarter': t('statistics.team.fallzahlen.quarter', 'Quartal'),
      'year': t('statistics.team.fallzahlen.year', 'Jahr')
    };
    const filterText = `${t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}: ${timeFilterLabels[timeFilter] || timeFilter}`;
    doc.text(filterText, 14, yPosition);
    yPosition += 5;
    
    if (startPeriod || endPeriod) {
      const rangeText = `${startPeriod ? formatPeriod(startPeriod) : t('statistics.team.fallzahlen.all', 'Alle')} - ${endPeriod ? formatPeriod(endPeriod) : t('statistics.team.fallzahlen.all', 'Alle')}`;
      doc.text(rangeText, 14, yPosition);
      yPosition += 5;
    }
    
    yPosition += 5;
    
    // Hole alle verfügbaren Perioden
    const allPeriods = availablePeriods.filter(period => {
      if (startPeriod && period < startPeriod) return false;
      if (endPeriod && period > endPeriod) return false;
      return true;
    });
    
    // Tabellendaten vorbereiten
    const tableData = [];
    
    allPeriods.forEach(period => {
      const row = [formatPeriod(period)];
      
      // Für jeden User den Count für diesen Zeitraum
      selectedUsers.forEach(user => {
        const userData = addendaData[user.id];
        if (!userData || !userData.addenda) {
          row.push(0);
          return;
        }

        let dataArray = [];
          switch (timeFilter) {
          case 'date': dataArray = userData.addenda.by_date; break;
          case 'week': dataArray = userData.addenda.by_week; break;
          case 'month': dataArray = userData.addenda.by_month; break;
          case 'quarter': dataArray = userData.addenda.by_quarter; break;
          case 'year': dataArray = userData.addenda.by_year; break;
          default: break;
        }

        const periodData = dataArray.find(d => d.period === period);
        row.push(periodData ? periodData.count : 0);
      });
      
      tableData.push(row);
    });
    
    // Tabellen-Header
    const tableHeaders = [t('statistics.team.fallzahlen.period', 'Zeitraum')];
    selectedUsers.forEach(user => {
      const userName = `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim();
      tableHeaders.push(userName);
    });
    
    // Tabelle hinzufügen
    doc.autoTable({
      startY: yPosition,
        head: [tableHeaders],
        body: tableData,
      theme: 'grid',
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [66, 66, 66], textColor: [255, 255, 255], fontStyle: 'bold' },
      margin: { left: 14, right: 14 },
      didDrawPage: () => {
          addFooter();
        }
      });

    // Fußzeile auf der letzten Seite (falls nicht schon durch didDrawPage hinzugefügt)
    if (doc.lastAutoTable.finalY < pageHeight - 20) {
      addFooter();
    }
    
    // Speichern
    doc.save('team_addenda_report.pdf');
  };

  // Prüfe ob User ausgewählt sind
  if (selectedUsers.length === 0) {
  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('statistics.team.categories.addenda', 'Addenda')}
      </Typography>

      {/* Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
        <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
          <Select
            value={timeFilter}
            onChange={handleTimeFilterChange}
            label={t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}
          >
            <MenuItem value="date">{t('statistics.team.fallzahlen.day', 'Tag')}</MenuItem>
            <MenuItem value="week">{t('statistics.team.fallzahlen.week', 'Woche')}</MenuItem>
            <MenuItem value="month">{t('statistics.team.fallzahlen.month', 'Monat')}</MenuItem>
            <MenuItem value="quarter">{t('statistics.team.fallzahlen.quarter', 'Quartal')}</MenuItem>
            <MenuItem value="year">{t('statistics.team.fallzahlen.year', 'Jahr')}</MenuItem>
          </Select>
        </FormControl>

        <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.team.fallzahlen.startPeriod', 'Von')}</InputLabel>
          <Select
            value={startPeriod}
            onChange={handleStartPeriodChange}
            label={t('statistics.team.fallzahlen.startPeriod', 'Von')}
          >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            {availablePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {formatPeriod(period)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.team.fallzahlen.endPeriod', 'Bis')}</InputLabel>
          <Select
            value={endPeriod}
            onChange={handleEndPeriodChange}
            label={t('statistics.team.fallzahlen.endPeriod', 'Bis')}
          >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
            {availablePeriods.map((period) => (
              <MenuItem key={period} value={period}>
                {formatPeriod(period)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={allUsers}
          value={selectedUsers}
          onChange={(event, newValue) => {
            setSelectedUsers(newValue);
          }}
          getOptionLabel={(user) => `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('statistics.team.selectUsers', 'Benutzer auswählen')}
              placeholder={t('statistics.team.selectUsersPlaceholder', 'Tippen zum Suchen...')}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((user, index) => (
              <Chip
                {...getTagProps({ index })}
                key={user.id}
                label={`${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
                size="small"
              />
            ))
          }
          sx={{ flex: 1 }}
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {t('statistics.team.selectUsersFirst', 'Bitte wählen Sie mindestens einen Benutzer aus.')}
      </Typography>
    </Box>
  );
  }

  if (addendaLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!addendaData) {
    return (
      <Box>
        <Typography variant="body2" color="text.secondary">
          {t('statistics.team.addenda.noData', 'Keine Daten verfügbar')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {t('statistics.team.categories.addenda', 'Addenda')}
      </Typography>

      {/* Filter */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
          <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}</InputLabel>
            <Select
              value={timeFilter}
              onChange={handleTimeFilterChange}
            label={t('statistics.team.fallzahlen.timePeriod', 'Zeitraum')}
          >
            <MenuItem value="date">{t('statistics.team.fallzahlen.day', 'Tag')}</MenuItem>
            <MenuItem value="week">{t('statistics.team.fallzahlen.week', 'Woche')}</MenuItem>
            <MenuItem value="month">{t('statistics.team.fallzahlen.month', 'Monat')}</MenuItem>
            <MenuItem value="quarter">{t('statistics.team.fallzahlen.quarter', 'Quartal')}</MenuItem>
            <MenuItem value="year">{t('statistics.team.fallzahlen.year', 'Jahr')}</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.team.fallzahlen.startPeriod', 'Von')}</InputLabel>
            <Select
              value={startPeriod}
              onChange={handleStartPeriodChange}
            label={t('statistics.team.fallzahlen.startPeriod', 'Von')}
            >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
              {availablePeriods.map((period) => (
                <MenuItem key={period} value={period}>
                  {formatPeriod(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ width: 120 }}>
          <InputLabel>{t('statistics.team.fallzahlen.endPeriod', 'Bis')}</InputLabel>
            <Select
              value={endPeriod}
              onChange={handleEndPeriodChange}
            label={t('statistics.team.fallzahlen.endPeriod', 'Bis')}
            >
            <MenuItem value="">{t('statistics.team.fallzahlen.all', 'Alle')}</MenuItem>
              {availablePeriods.map((period) => (
                <MenuItem key={period} value={period}>
                  {formatPeriod(period)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

        <Autocomplete
          multiple
          disableCloseOnSelect
          options={allUsers}
          value={selectedUsers}
          onChange={(event, newValue) => {
            setSelectedUsers(newValue);
          }}
          getOptionLabel={(user) => `${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
          renderInput={(params) => (
            <TextField
              {...params}
              label={t('statistics.team.selectUsers', 'Benutzer auswählen')}
              placeholder={t('statistics.team.selectUsersPlaceholder', 'Tippen zum Suchen...')}
            />
          )}
          renderTags={(value, getTagProps) =>
            value.map((user, index) => (
              <Chip
                {...getTagProps({ index })}
                key={user.id}
                label={`${user.academic_degree || ''} ${user.first_name || ''} ${user.last_name || ''}`.trim()}
                size="small"
              />
            ))
          }
          sx={{ flex: 1 }}
        />
        </Box>

      {/* Chart mit Custom Y-Achsen-Labels */}
      <Paper sx={{ p: 2, mb: 2 }}>
        {chartData.length > 0 ? (
          <Box sx={{ display: 'flex', gap: 2 }}>
            {/* Custom Y-Achsen-Labels (User-Namen) */}
            <Box sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              minWidth: 200,
              marginTop: '20px',
              marginBottom: '0px'
            }}>
              {chartData.map((data, index) => {
                const user = selectedUsers.find(u => u.id === data.userId);
                if (!user) return null;
                
                // Berechne die Höhe basierend auf der Chart-Margin
                const totalHeight = Math.max(400, selectedUsers.length * 100);
                const chartMarginTop = 20;
                const chartMarginBottom = 75;
                const yAxisHeight = totalHeight - chartMarginTop - chartMarginBottom;
                const userHeight = yAxisHeight / selectedUsers.length;
                
                return (
                  <Box 
                    key={data.userId}
                    sx={{ 
                      height: `${userHeight}px`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'flex-end',
                      pr: 2,
                      borderBottom: index < selectedUsers.length - 1 ? '1px solid rgba(0,0,0,0.05)' : 'none'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        textAlign: 'right',
                        fontSize: '0.875rem',
                        lineHeight: 1.3
                      }}
                    >
                      <Box component="span" sx={{ fontWeight: 600, display: 'block' }}>
                        {user.academic_degree || ''}
                      </Box>
                      <Box component="span" sx={{ display: 'block' }}>
                        {user.first_name} {user.last_name}
      </Box>
        </Typography>
                  </Box>
                );
              })}
            </Box>

            {/* Chart ohne Y-Achsen Labels */}
            <Box ref={chartRef} sx={{ flex: 1, height: Math.max(400, selectedUsers.length * 100) }}>
              <BarChart
              dataset={chartData}
                layout="horizontal"
                yAxis={[{ 
                  dataKey: 'user',
                  scaleType: 'band',
                  categoryGapRatio: 0.3,
                  barGapRatio: 0.1
                }]}
              xAxis={[{ 
                min: 0,
                max: (() => {
                    if (chartData.length === 0) return 10;
                  const maxValue = Math.max(...chartData.map(d => d.addendaCount));
                    return maxValue > 0 ? Math.ceil(maxValue * 1.1) : 10;
                  })(),
                  tickMinStep: 1
                }]}
                tooltip={{
                  trigger: 'item'
                }}
                sx={{
                  '.MuiChartsLegend-root': {
                    display: 'none !important'
                  },
                  '.MuiChartsAxis-left .MuiChartsAxis-tickLabel': {
                    display: 'none !important'
                  },
                  '.MuiChartsAxis-left .MuiChartsAxis-line': {
                    display: 'none !important'
                  }
                }}
                series={[{
                  type: 'bar',
                  id: 'addendaCount',
                  dataKey: 'addendaCount',
                  label: t('statistics.personal.addenda.addenda', 'Addenda'),
                  color: 'var(--rd-chart-1)',
                  valueFormatter: (value) => `${value}`
                }]}
                margin={{ top: 20, right: 30, bottom: 50, left: 10 }}
                grid={{ vertical: true, horizontal: false }}
              />
            </Box>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            {t('statistics.team.addenda.noData', 'Keine Daten zum Anzeigen')}
          </Typography>
        )}
      </Paper>
    </Box>
  );
});

export default Addenda;
